import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { BADGES } from "@/lib/categories";
import { getAvatarUrl, initials, formatDate } from "@/lib/helpers";
import { Loader2, Users, MapPin, Search, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Members() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/members");
        setMembers(data.members);
      } catch {
        toast.error("Could not load members");
      }
      setLoading(false);
    })();
  }, []);

  const term = q.trim().toLowerCase();
  const filtered = term
    ? members.filter((m) =>
        `${m.first_name} ${m.last_name}`.toLowerCase().includes(term) ||
        (m.zip_code || "").toLowerCase().includes(term)
      )
    : members;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-semibold text-[#2A9D8F] tracking-widest uppercase flex items-center gap-2">
          <Users className="w-4 h-4" /> Community
        </p>
        <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-bold text-[#1B2A4A] leading-tight">
          Your <span className="text-[#E8621A]">neighbors.</span>
        </h1>
        <p className="mt-3 text-[#1B2A4A]/70 max-w-xl">
          Everyone helping to make this community what it is. {members.length} member{members.length === 1 ? "" : "s"} strong.
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1B2A4A]/40" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or zip..."
          data-testid="members-search"
          className="pl-10 h-11 rounded-full bg-white border-[#1B2A4A]/15 focus-visible:ring-2 focus-visible:ring-[#E8621A]/50"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#E8621A]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-[#1B2A4A]/15">
          <p className="text-[#1B2A4A]/60">No members match "{q}".</p>
        </div>
      ) : (
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.03 } }, hidden: {} }}
        >
          {filtered.map((m) => (
            <motion.div
              key={m.id}
              data-testid={`member-card-${m.id}`}
              variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0 } }}
              className="bg-white rounded-3xl border border-[#1B2A4A]/8 p-5 flex items-start gap-4"
            >
              <Avatar className="w-14 h-14 shrink-0">
                <AvatarImage src={getAvatarUrl(m.avatar_path)} />
                <AvatarFallback className="bg-[#2A9D8F] text-white text-sm font-bold">
                  {initials(m.first_name, m.last_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heading font-bold text-[#1B2A4A] truncate">
                    {m.first_name} {m.last_name}
                  </p>
                  {m.role === "admin" && (
                    <span className="text-[10px] bg-[#2A9D8F]/15 text-[#2A9D8F] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-widest">
                      Admin
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-[#1B2A4A]/60 flex-wrap">
                  {m.zip_code && (
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.zip_code}</span>
                  )}
                  <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Joined {formatDate(m.member_since)}</span>
                </div>
                <div className="mt-3 flex gap-4 text-xs">
                  <span className="text-[#1B2A4A]/85"><span className="font-bold text-[#1B2A4A]">{m.tips_count}</span> tips</span>
                  <span className="text-[#1B2A4A]/85 flex items-center gap-1">
                    <Heart className="w-3 h-3 text-[#E76F51]" /> <span className="font-bold text-[#1B2A4A]">{m.likes_received}</span> likes
                  </span>
                </div>
                {m.badges?.length > 0 && (
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {m.badges.slice(0, 5).map((b) => (
                      <span key={b} title={BADGES[b]?.label} className="text-base leading-none">{BADGES[b]?.icon || "✨"}</span>
                    ))}
                    {m.badges.length > 5 && <span className="text-xs text-[#1B2A4A]/50">+{m.badges.length - 5}</span>}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
